/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   TenantLicensesPage.tsx
 *     Description     :   Spec 285 T032, read-only since the Spec 286 cut-over (FR-003, T064) —
 *                         SuperAdmin-only view of ONE tenant's licence as Fabric Control Management
 *                         (FCM) wrote it: status, tier, limits, each add-on with its term and whether
 *                         it is in force, and the growth freeze. FCM is the only writer; this page
 *                         offers no edit. One tenant at a time (ADR 0091 — a SuperAdmin selects a
 *                         context; nothing here reads across tenants). Defaults to the SuperAdmin's
 *                         current tenant, resolved server-side. Built from the canonical kit + tokens
 *                         (Principle XLV).
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import {
  PageLayout,
  Card,
  DescriptionList,
  Button,
  Input,
  Badge,
  Alert,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { tenantLicenseService, type TenantLicenseRecord } from '@/services/tenantLicenseService';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ADD_ONS: Array<{ key: 'backup' | 'ops'; title: string; unlocks: string }> = [
  { key: 'backup', title: 'Backup add-on', unlocks: 'Backup Management and the backup tiles on the main dashboard' },
  { key: 'ops', title: 'Ops add-on', unlocks: 'Pipeline Monitoring' },
];

const STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'error' }> = {
  active: { label: 'Active', variant: 'success' },
  suspended: { label: 'Suspended', variant: 'warning' },
  revoked: { label: 'Revoked', variant: 'error' },
};

const SOURCE: Record<string, string> = {
  fcm: 'FCM',
  manual: 'Manual (written before the cut-over)',
  none: 'No licence row',
};

const count = new Intl.NumberFormat('en-US');

function statusBadge(status: string | null) {
  if (status == null) return <Badge variant="default" size="sm">No licence</Badge>;
  const s = STATUS[status];
  return <Badge variant={s?.variant ?? 'default'} size="sm">{s?.label ?? status}</Badge>;
}

/** A limit as FCM wrote it: null is "no limit"; with no licence row there is no limit to show. */
function limitText(row: TenantLicenseRecord, value: number | null): string {
  if (row.license_status == null) return '—';
  return value == null ? 'No limit' : count.format(value);
}

function capitalize(value: string | null): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '—';
}

function addOnTerm(enabled: boolean, until: string | null): string {
  if (!enabled) return 'Not included';
  return until ? `Included until ${formatEasternDate(until)}` : 'Included, no end date';
}

const TenantLicensesPage: React.FC = () => {
  // null ⇒ the SuperAdmin's current tenant (resolved server-side); otherwise an explicit id.
  const [targetTenantId, setTargetTenantId] = useState<string | null>(null);
  const [tenantInput, setTenantInput] = useState('');
  const [tenantInputError, setTenantInputError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery<TenantLicenseRecord>({
    queryKey: ['tenant-license', targetTenantId ?? 'current'],
    queryFn: () => (targetTenantId ? tenantLicenseService.get(targetTenantId) : tenantLicenseService.getCurrent()),
  });

  const loadTenant = () => {
    const value = tenantInput.trim();
    if (!GUID.test(value)) {
      setTenantInputError('Enter a tenant id in GUID form (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx).');
      return;
    }
    setTenantInputError(null);
    setTargetTenantId(value);
  };

  const showCurrentTenant = () => {
    setTenantInput('');
    setTenantInputError(null);
    setTargetTenantId(null);
  };

  return (
    <PageLayout
      title="Tenant Licenses"
      description="One tenant's licence as Fabric Control Management issued it. One tenant at a time; SuperAdmin only."
      icon={<Lock className="h-6 w-6" />}
      helpId="admin.tenant-licenses"
      onRefresh={() => { void refetch(); }}
      isRefreshing={isFetching}
      lastFetchedAt={dataUpdatedAt || null}
      refreshError={isError ? error : undefined}
    >
      <div className="space-y-4">
        <Alert variant="info" title="Read-only">
          Licences are managed in Fabric Control Management (FCM). To change a tenant&apos;s licence, use FCM; the
          change shows here on the next refresh.
        </Alert>

        <Card padding="md">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1">
              <Input
                label="Tenant ID"
                placeholder={targetTenantId ? 'Enter another tenant id…' : 'Showing your current tenant — enter an id to view another…'}
                value={tenantInput}
                onChange={(e) => setTenantInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadTenant(); }}
                error={!!tenantInputError}
                helperText={tenantInputError ?? undefined}
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="primary" onClick={loadTenant}>Load tenant</Button>
              <Button variant="ghost" onClick={showCurrentTenant} disabled={targetTenantId === null}>
                My current tenant
              </Button>
            </div>
          </div>
        </Card>

        {isLoading ? (
          <LoadingState rows={4} />
        ) : isError || !data ? (
          <ErrorState
            title="Could not load this tenant's licence"
            message="The tenant may not exist, or you may not have SuperAdmin access."
            onRetry={() => { void refetch(); }}
          />
        ) : (
          <Card padding="md" title={targetTenantId ? 'Selected tenant' : 'Your current tenant'}>
            <div className="space-y-5">
              <DescriptionList
                columns={2}
                items={[
                  { label: 'Tenant ID', value: <span className="font-mono text-sm">{data.platform_tenant_id}</span> },
                  { label: 'Status', value: statusBadge(data.license_status) },
                  { label: 'Tier', value: capitalize(data.usage_tier) },
                  { label: 'Source', value: SOURCE[data.source] ?? data.source },
                  { label: 'Workspace limit', value: <span className="tabular-nums">{limitText(data, data.limit_workspaces)}</span> },
                  { label: 'Item limit', value: <span className="tabular-nums">{limitText(data, data.limit_items)}</span> },
                  { label: 'Capacity limit', value: <span className="tabular-nums">{limitText(data, data.limit_capacities)}</span> },
                  { label: 'Growth freeze', value: data.growth_frozen_from ? `Frozen from ${formatEasternDate(data.growth_frozen_from)}` : 'Not frozen' },
                  { label: 'Last changed', value: data.updated_at ? formatEastern(data.updated_at) : '—' },
                ]}
              />

              <div className="space-y-4 border-t border-[var(--color-border)] pt-4">
                {ADD_ONS.map((a) => {
                  const inForce = a.key === 'backup' ? data.backup_in_force : data.ops_in_force;
                  const term = a.key === 'backup'
                    ? addOnTerm(data.backup_enabled, data.backup_until)
                    : addOnTerm(data.ops_enabled, data.ops_until);
                  return (
                    <div key={a.key} data-testid={`addon-${a.key}`} className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <span className="font-medium text-[var(--color-text-primary)]">{a.title}</span>
                        <p className="text-sm text-[var(--color-text-secondary)]">{term}. Unlocks {a.unlocks}.</p>
                      </div>
                      <Badge variant={inForce ? 'success' : 'default'} size="sm">{inForce ? 'In force' : 'Not in force'}</Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}
      </div>
    </PageLayout>
  );
};

export default TenantLicensesPage;
